// Tenancy mode + runtime tenant identity for the React SPA.
//
// Build-time flag (legacy):
//   TENANT_MODE — driven by REACT_APP_TENANT_MODE at build time.
//
//     "internal" (default) — full app: includes /legacy, /dev, /command-center,
//                            ConfigPage Infrastructure + LegacySync sections.
//                            Used for the founder's own dev + staging instance.
//     "hosted"             — paying-customer build: hides everything that
//                            touches localhost-only services.
//
//   Production tenant builds (provision_tenant_shared.sh) set
//   REACT_APP_TENANT_MODE=hosted before `npm run build`.
//
// Runtime tenant identity (multi-tenancy refactor PR5):
//   getTenantSlug() — extracts the tenant slug from window.location.hostname:
//     - <slug>.app.goeswestservices.com  → "<slug>"
//     - <custom-domain> (anything else)  → null
//       (Backend resolves via Tenant.custom_domain — added in PR4.)
//     - localhost / 127.0.0.1            → process.env.REACT_APP_TENANT_SLUG
//       (Dev override; unset = null, meaning "no tenant claim from frontend"
//       and the dev backend can default to tenant_id=1.)
//
//   isCustomDomain() — true when the SPA is being served at a non-platform host.
//   IS_HOSTED_TENANT, IS_INTERNAL — preserved for back-compat with App.tsx.

export const TENANT_MODE: 'internal' | 'hosted' =
  process.env.REACT_APP_TENANT_MODE === 'hosted' ? 'hosted' : 'internal';

export const IS_HOSTED_TENANT = TENANT_MODE === 'hosted';
export const IS_INTERNAL = TENANT_MODE === 'internal';

const APP_DOMAIN = 'app.goeswestservices.com';
const APP_DOMAIN_SUFFIX = '.' + APP_DOMAIN;
const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '0.0.0.0', '']);

/**
 * Parse a hostname (without port) and return the tenant slug if the host
 * is a `<slug>.app.goeswestservices.com` subdomain.
 *
 * Returns null for:
 *   - Apex / `www` / multi-level subdomains under the platform domain
 *   - Anything not ending in `.app.goeswestservices.com`
 *
 * Pure function; used by getTenantSlug() and exported for testing.
 */
export function extractSlugFromHostname(hostname: string): string | null {
  if (!hostname) return null;
  const h = hostname.toLowerCase().split(':')[0];
  if (!h.endsWith(APP_DOMAIN_SUFFIX)) return null;
  const sub = h.slice(0, -APP_DOMAIN_SUFFIX.length);
  if (!sub || sub === 'www' || sub.includes('.')) return null;
  return sub;
}

/**
 * Resolve the tenant slug from the current browser URL.
 *
 * Order:
 *   1. `process.env.REACT_APP_TENANT_SLUG` if set (build-time override —
 *      lets a tenant-pinned bundle deploy to ANY host, including
 *      `gws-<slug>.web.app` Firebase default sites before a custom
 *      domain is wired up)
 *   2. localhost / 127.0.0.1 → null (no env override = no tenant)
 *   3. `<slug>.app.goeswestservices.com` → slug
 *   4. Anything else (custom domain) → null
 *
 * Returns null in SSR / non-browser contexts (no window).
 */
export function getTenantSlug(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  if (process.env.REACT_APP_TENANT_SLUG) {
    return process.env.REACT_APP_TENANT_SLUG;
  }
  const hostname = window.location.hostname;
  if (LOCAL_HOSTS.has(hostname)) {
    return null;
  }
  return extractSlugFromHostname(hostname);
}

/**
 * True when the SPA is served at a host that's NOT a platform subdomain
 * and NOT a local-dev host. Used by TenantContext to flag the SLA-tier
 * custom-domain case (backend resolves via Tenant.custom_domain).
 */
export function isCustomDomain(): boolean {
  if (typeof window === 'undefined' || !window.location) return false;
  // Build-time tenant pin wins over hostname inference — a pinned bundle
  // is never in custom-domain mode regardless of where it's served.
  if (process.env.REACT_APP_TENANT_SLUG) return false;
  const hostname = window.location.hostname.toLowerCase().split(':')[0];
  if (LOCAL_HOSTS.has(hostname)) return false;
  // Apex `app.goeswestservices.com` is platform-owned (workspace router),
  // not a customer custom-domain. Treat as not-custom.
  if (hostname === APP_DOMAIN) return false;
  return !hostname.endsWith(APP_DOMAIN_SUFFIX);
}
